import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import CorporateSelect from '../../../components/ui/CorporateSelect';
import {
  getLineasPendientes,
  vincularLineaAProducto,
  ignorarLinea,
  buscarProductosParaVincular,
  procesarVinculacionesPendientes,
  getIndicadoresVinculacion,
} from '../../../services/vinculacionService';
import { productosService, calcularPvpTeorico, calcularPvpSugerido, calcularCosteConIva, calcularCosteConImpuestos, buscarDuplicados } from '../../../services/productService';
import { tiendaDb } from '../../../lib/supabase';

const ACCENT = '#D6EF4E';
const ORANGE = '#FB923C';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const MARGEN_DEFAULT = 35;
const RECARGO_DEFAULT = 1.4;

const fmt = (n) => (n != null && !isNaN(n)) ? `${Number(n)?.toFixed(2)} €` : '—';
const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';

const IVA_OPTS = [
  { value: '0', label: '0%' },
  { value: '4', label: '4%' },
  { value: '10', label: '10%' },
  { value: '21', label: '21%' },
];
const UNIDAD_OPTS = ['UNIDAD', 'KG', 'GRAMO', 'LITRO', 'CAJA', 'PAQUETE', 'PIEZA']?.map(u => ({ value: u, label: u }));

// ─── MODAL BUSCAR PRODUCTO ────────────────────────────────────────────────────
const BuscarProductoModal = ({ linea, onVincular, onClose }) => {
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  const [error, setError] = useState(null);

  const buscar = useCallback(async (q) => {
    if (!q?.trim() || q?.trim()?.length < 2) { setResultados([]); return; }
    setBuscando(true);
    try {
      const res = await buscarProductosParaVincular(q);
      setResultados(res);
    } catch (e) {
      setError(e?.message);
    } finally {
      setBuscando(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscar(query), 300);
    return () => clearTimeout(t);
  }, [query, buscar]);

  const handleVincular = async () => {
    if (!seleccionado) return;
    setVinculando(true);
    setError(null);
    try {
      await onVincular(linea, seleccionado);
      onClose();
    } catch (e) {
      setError(e?.message || 'Error al vincular');
    } finally {
      setVinculando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)' }}>
      <div className="w-full max-w-xl rounded-2xl flex flex-col max-h-[85vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}>
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-base">Buscar producto del catálogo</h3>
            <p className="font-oranienbaum text-white/40 text-xs mt-0.5 truncate max-w-xs">{linea?.descripcion_original || linea?.descripcion}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="px-5 py-4 flex-shrink-0">
          <div className="relative">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              autoFocus
              type="text"
              placeholder="Buscar por nombre, EAN, PLU, código..."
              value={query}
              onChange={e => setQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-5 pb-4 space-y-1.5">
          {buscando && (
            <div className="flex items-center justify-center py-6">
              <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
            </div>
          )}
          {!buscando && resultados?.length === 0 && query?.length >= 2 && (
            <p className="font-oranienbaum text-white/30 text-sm text-center py-6">Sin resultados para "{query}"</p>
          )}
          {resultados?.map(p => (
            <button
              key={p?.id}
              onClick={() => setSeleccionado(p)}
              className="w-full text-left p-3 rounded-xl transition-all"
              style={seleccionado?.id === p?.id
                ? { background: `${ACCENT}15`, border: `1px solid ${ACCENT}40` }
                : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-oranienbaum text-white text-sm font-semibold truncate">{p?.nombre}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {p?.ean && <span className="font-oranienbaum text-white/40 text-[10px]">EAN: {p?.ean}</span>}
                    {p?.codigo_epelsa && <span className="font-oranienbaum text-white/40 text-[10px]">Epelsa: {p?.codigo_epelsa}</span>}
                    {p?.proveedor?.nombre && <span className="font-oranienbaum text-white/40 text-[10px]">{p?.proveedor?.nombre}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {p?.pvp_venta && <p className="font-oranienbaum text-white/70 text-xs">{fmt(p?.pvp_venta)}</p>}
                  {p?.precio_compra && <p className="font-oranienbaum text-white/40 text-[10px]">Coste: {fmt(p?.precio_compra)}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div className="px-5 pb-3">
            <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button
            onClick={handleVincular}
            disabled={!seleccionado || vinculando}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-oranienbaum font-bold transition-all disabled:opacity-40"
            style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
            {vinculando ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#D6EF4E] animate-spin" />Vinculando...</> : <><Icon name="Link" size={14} />Vincular</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL CREAR PRODUCTO DESDE FACTURA ──────────────────────────────────────
const CrearProductoModal = ({ linea, onCreado, onClose }) => {
  const [familias, setFamilias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [duplicados, setDuplicados] = useState([]);
  const [buscandoDuplicados, setBuscandoDuplicados] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);

  // Pre-cargar datos de la línea de factura
  const precioSinIva = linea?.precio_compra_neto || linea?.precio_unitario || 0;
  const ivaLinea = linea?.porcentaje_iva ?? 10;

  const [form, setForm] = useState({
    nombre: linea?.descripcion_original || linea?.descripcion || '',
    descripcion: '',
    ean: linea?.ean_proveedor || '',
    referencia_proveedor: linea?.referencia_proveedor || '',
    descripcion_proveedor: linea?.descripcion_original || linea?.descripcion || '',
    familia_id: '',
    proveedor_principal_id: linea?.proveedor_id || '',
    unidad_venta: linea?.unidad || 'UNIDAD',
    tipo_iva: String(ivaLinea),
    recargo_equivalencia: String(RECARGO_DEFAULT),
    precio_compra: precioSinIva > 0 ? String(precioSinIva?.toFixed(4)) : '',
    margen_objetivo: String(MARGEN_DEFAULT),
    pvp_venta: '',
    activo: true,
  });

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Cálculos reactivos
  const coste = parseFloat(form?.precio_compra) || null;
  const iva = parseFloat(form?.tipo_iva) || 10;
  const recargo = parseFloat(form?.recargo_equivalencia) || RECARGO_DEFAULT;
  const margenDec = form?.margen_objetivo !== '' ? parseFloat(form?.margen_objetivo) / 100 : MARGEN_DEFAULT / 100;
  const costeConIva = coste ? calcularCosteConIva(coste, iva) : null;
  const costeConImpuestos = coste ? calcularCosteConImpuestos(coste, iva, recargo) : null;
  const pvpTeorico = coste ? calcularPvpTeorico(coste, margenDec) : null;
  const pvpSugerido = coste ? calcularPvpSugerido(coste, margenDec, iva, 'TERMINAR_99') : null;
  const pvpActualNum = parseFloat(form?.pvp_venta) || pvpSugerido || null;

  // Familia seleccionada (para mostrar info Epelsa)
  const familiaSeleccionada = familias?.find(f => f?.id === form?.familia_id);
  const tieneMapeoEpelsa = !!familiaSeleccionada?.epelsa_family_code;

  useEffect(() => {
    (async () => {
      const [fams, provs] = await Promise.all([
        tiendaDb?.from('familias_producto')?.select('id, nombre, epelsa_family_code, epelsa_sync_enabled')?.eq('activo', true)?.order('nombre'),
        tiendaDb?.from('proveedores')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
      ]);
      setFamilias(fams?.data || []);
      setProveedores(provs?.data || []);
    })();
  }, []);

  // Buscar duplicados cuando cambian campos clave
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!form?.ean && !form?.referencia_proveedor) return;
      setBuscandoDuplicados(true);
      try {
        const dups = await buscarDuplicados({
          ean: form?.ean,
          referenciaProveedor: form?.referencia_proveedor,
        });
        setDuplicados(dups);
      } catch (e) { /* silencioso */ }
      setBuscandoDuplicados(false);
    }, 600);
    return () => clearTimeout(t);
  }, [form?.ean, form?.referencia_proveedor]);

  const handleGuardar = async () => {
    if (!form?.nombre?.trim()) { setError('El nombre es obligatorio'); return; }
    if (!form?.familia_id) { setError('La familia es obligatoria para poder sincronizar con Epelsa'); return; }
    setGuardando(true);
    setError(null);
    console.log('[HANDLE GUARDAR v4] iniciando creación de producto', new Date()?.toISOString());
    try {
      const pvpFinal = pvpActualNum ? parseFloat(pvpActualNum?.toFixed(2)) : null;
      const productoPayload = {
        nombre: form?.nombre?.trim(),
        descripcion: form?.descripcion || null,
        // codigo_interno se genera automáticamente en productosService.crearDesdeFactura
        ean: form?.ean || null,
        referencia_proveedor: form?.referencia_proveedor || null,
        descripcion_proveedor: form?.descripcion_proveedor || null,
        familia_id: form?.familia_id || null,
        proveedor_principal_id: form?.proveedor_principal_id || null,
        unidad_venta: form?.unidad_venta || 'UNIDAD',
        unidad_compra: form?.unidad_venta || 'UNIDAD',
        tipo_iva: iva,
        recargo_equivalencia: recargo,
        precio_compra: coste,
        precio_compra_con_iva: costeConIva,
        coste_con_impuestos: costeConImpuestos,
        margen_objetivo: margenDec,
        pvp_venta: pvpFinal,
        pvp_sugerido: pvpSugerido ? parseFloat(pvpSugerido?.toFixed(2)) : null,
        pvp_teorico: pvpTeorico ? parseFloat(pvpTeorico?.toFixed(4)) : null,
        activo: form?.activo,
      };

      const resultado = await productosService?.crearDesdeFactura({
        productoPayload,
        lineaId: linea?.id,
        facturaId: linea?.factura_id,
        proveedorId: form?.proveedor_principal_id || linea?.proveedor_id,
        precioSinIva: coste,
        iva,
        recargo,
        descuentoPct: linea?.descuento_porcentaje || 0,
      });

      const codigoGenerado = resultado?.codigo_interno || resultado;
      setExito(`✓ Producto creado y vinculado · Código ERP: ${codigoGenerado} · Código Epelsa: se asignará automáticamente`);
      setTimeout(() => {
        onCreado?.();
        onClose();
      }, 3000);
    } catch (e) {
      setError(e?.message || 'Error al crear el producto');
    } finally {
      setGuardando(false);
    }
  };

  const famOpts = [
    { value: '', label: '— Seleccionar familia (obligatorio) —' },
    ...familias?.map(f => ({
      value: f?.id,
      label: f?.epelsa_family_code
        ? `${f?.nombre} (Epelsa: ${f?.epelsa_family_code})`
        : `${f?.nombre} ⚠ Sin mapeo Epelsa`,
    })),
  ];
  const provOpts = [{ value: '', label: '— Sin proveedor —' }, ...proveedores?.map(p => ({ value: p?.id, label: p?.nombre }))];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)' }}>
      <div className="w-full max-w-2xl rounded-2xl flex flex-col max-h-[92vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="PackagePlus" size={15} style={{ color: GOLD }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">Crear producto desde factura</h3>
              <p className="font-oranienbaum text-white/40 text-xs mt-0.5">Se vinculará automáticamente al guardar</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {/* Info línea origen */}
          <div className="p-3 rounded-xl" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
            <p className="font-oranienbaum text-white/40 text-[10px] uppercase tracking-wider mb-1">Línea de factura origen</p>
            <p className="font-oranienbaum text-white/80 text-sm">{linea?.descripcion_original || linea?.descripcion}</p>
            <div className="flex gap-4 mt-1">
              {linea?.referencia_proveedor && <span className="font-oranienbaum text-white/40 text-xs">Ref: {linea?.referencia_proveedor}</span>}
              {linea?.ean_proveedor && <span className="font-oranienbaum text-white/40 text-xs">EAN: {linea?.ean_proveedor}</span>}
              {precioSinIva > 0 && <span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>Precio: {fmt(precioSinIva)}</span>}
            </div>
          </div>

          {/* Alerta duplicados */}
          {duplicados?.length > 0 && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={14} style={{ color: ORANGE }} />
                <p className="font-oranienbaum text-sm font-bold" style={{ color: ORANGE }}>Este artículo podría existir ya</p>
              </div>
              {duplicados?.map(d => (
                <div key={d?.id} className="flex items-center justify-between py-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <p className="font-oranienbaum text-white/80 text-xs">{d?.nombre}</p>
                    <p className="font-oranienbaum text-white/40 text-[10px]">{d?.motivo}</p>
                  </div>
                  <span className="font-oranienbaum text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
                    {d?.codigo_interno || d?.ean || d?.plu || '—'}
                  </span>
                </div>
              ))}
              <p className="font-oranienbaum text-white/40 text-[10px] mt-2">Verifica antes de crear para evitar duplicados.</p>
            </div>
          )}

          {/* Éxito */}
          {exito && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle2" size={14} style={{ color: ACCENT }} />
                <p className="font-oranienbaum text-sm" style={{ color: ACCENT }}>{exito}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
            </div>
          )}

          {/* Datos generales */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Datos generales</p>
            <div className="space-y-2">
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre *</label>
                <input type="text" value={form?.nombre} onChange={e => set('nombre', e?.target?.value)} className={inputCls} style={inputSt} placeholder="Nombre del producto" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1">EAN / Barras</label>
                  <input type="text" value={form?.ean} onChange={e => set('ean', e?.target?.value)} className={inputCls} style={inputSt} />
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1">Referencia proveedor</label>
                  <input type="text" value={form?.referencia_proveedor} onChange={e => set('referencia_proveedor', e?.target?.value)} className={inputCls} style={inputSt} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1">Unidad de venta</label>
                  <CorporateSelect value={form?.unidad_venta} onChange={v => set('unidad_venta', v)} options={UNIDAD_OPTS} />
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1">Proveedor</label>
                  <CorporateSelect value={form?.proveedor_principal_id} onChange={v => set('proveedor_principal_id', v)} options={provOpts} searchable />
                </div>
              </div>
            </div>
          </div>

          {/* Familia — OBLIGATORIA */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Clasificación</p>
            <div>
              <label className="block font-oranienbaum text-xs mb-1">
                <span className="text-white/50">Familia</span>
                <span className="ml-1 font-bold" style={{ color: RED }}>* obligatorio</span>
              </label>
              <CorporateSelect value={form?.familia_id} onChange={v => set('familia_id', v)} options={famOpts} searchable />
              {/* Info Epelsa según familia seleccionada */}
              {form?.familia_id && (
                <div className="mt-1.5 px-3 py-2 rounded-lg" style={{
                  background: tieneMapeoEpelsa ? 'rgba(214,239,78,0.06)' : 'rgba(251,146,60,0.06)',
                  border: `1px solid ${tieneMapeoEpelsa ? 'rgba(214,239,78,0.20)' : 'rgba(251,146,60,0.20)'}`,
                }}>
                  {tieneMapeoEpelsa ? (
                    <div className="flex items-center gap-2">
                      <Icon name="CheckCircle2" size={12} style={{ color: GOLD }} />
                      <p className="font-oranienbaum text-xs" style={{ color: GOLD }}>
                        Código Epelsa se asignará automáticamente en el rango{' '}
                        <strong>{familiaSeleccionada?.epelsa_family_code * 100}–{familiaSeleccionada?.epelsa_family_code * 100 + 99}</strong>
                        {' '}(familia Epelsa {familiaSeleccionada?.epelsa_family_code})
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={12} style={{ color: ORANGE }} />
                      <p className="font-oranienbaum text-xs" style={{ color: ORANGE }}>
                        Esta familia no tiene mapeo Epelsa. El producto se creará pero <strong>no se sincronizará</strong> hasta configurar el código Epelsa de la familia.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Impuestos */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Impuestos</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">IVA (%)</label>
                <CorporateSelect value={form?.tipo_iva} onChange={v => set('tipo_iva', v)} options={IVA_OPTS} />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Recargo equiv. (%)</label>
                <input type="number" value={form?.recargo_equivalencia} onChange={e => set('recargo_equivalencia', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" />
              </div>
            </div>
          </div>

          {/* Precios */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Precios</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Coste sin IVA (€)</label>
                <input type="number" value={form?.precio_compra} onChange={e => set('precio_compra', e?.target?.value)} className={inputCls} style={inputSt} step="0.0001" min="0" placeholder="0,00" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Margen objetivo (%)</label>
                <input type="number" value={form?.margen_objetivo} onChange={e => set('margen_objetivo', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" max="99" />
              </div>
            </div>
            {/* Precios calculados */}
            {coste && (
              <div className="grid grid-cols-4 gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-center">
                  <p className="font-oranienbaum text-xs font-bold" style={{ color: BLUE }}>{fmt(costeConIva)}</p>
                  <p className="font-oranienbaum text-white/30 text-[10px]">Coste c/IVA</p>
                </div>
                <div className="text-center">
                  <p className="font-oranienbaum text-xs font-bold" style={{ color: '#A78BFA' }}>{fmt(costeConImpuestos)}</p>
                  <p className="font-oranienbaum text-white/30 text-[10px]">Coste c/Imp.</p>
                </div>
                <div className="text-center">
                  <p className="font-oranienbaum text-xs font-bold" style={{ color: GOLD }}>{fmt(pvpTeorico)}</p>
                  <p className="font-oranienbaum text-white/30 text-[10px]">PVP teórico</p>
                </div>
                <div className="text-center">
                  <p className="font-oranienbaum text-xs font-bold" style={{ color: ACCENT }}>{fmt(pvpSugerido)}</p>
                  <p className="font-oranienbaum text-white/30 text-[10px]">PVP sugerido</p>
                </div>
              </div>
            )}
            <div className="mt-2">
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">
                PVP final (€) <span style={{ color: GOLD }}>por defecto: PVP sugerido</span>
              </label>
              <input
                type="number"
                value={form?.pvp_venta || (pvpSugerido ? pvpSugerido?.toFixed(2) : '')}
                onChange={e => set('pvp_venta', e?.target?.value)}
                className={inputCls}
                style={inputSt}
                step="0.01"
                min="0"
                placeholder={pvpSugerido ? pvpSugerido?.toFixed(2) : '0,00'}
              />
            </div>
          </div>

          {/* Código interno — informativo, no editable */}
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-start gap-2">
              <Icon name="Hash" size={13} className="text-white/30 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-oranienbaum text-white/50 text-xs font-semibold">Códigos asignados automáticamente</p>
                <p className="font-oranienbaum text-white/30 text-[10px] mt-0.5">
                  <strong className="text-white/50">Código interno ERP</strong> (LDD-XXXXXX): se genera al guardar.
                </p>
                <p className="font-oranienbaum text-white/30 text-[10px] mt-0.5">
                  <strong className="text-white/50">Código Epelsa / PLU</strong>: el agente local lo asignará automáticamente según la familia seleccionada.
                </p>
              </div>
            </div>
          </div>

          {/* Estado Epelsa */}
          <div className="p-3 rounded-xl" style={{ background: 'rgba(214,239,78,0.05)', border: '1px solid rgba(214,239,78,0.15)' }}>
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={13} style={{ color: GOLD }} />
              <p className="font-oranienbaum text-xs" style={{ color: GOLD }}>
                Estado inicial: <strong>PENDIENTE ALTA EPELSA</strong> — el agente local asignará el código y sincronizará con la báscula
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando || !!exito || !form?.familia_id}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-oranienbaum font-bold transition-all disabled:opacity-40"
            style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
            {guardando
              ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#D6EF4E] animate-spin" />Creando...</>
              : <><Icon name="PackagePlus" size={14} />Crear y vincular</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const ProductosVincular = () => {
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indicadores, setIndicadores] = useState({});
  const [procesando, setProcesando] = useState(false);
  const [procesandoMsg, setProcesandoMsg] = useState(null);
  const [buscarModal, setBuscarModal] = useState(null);
  const [crearModal, setCrearModal] = useState(null);
  const [accionando, setAccionando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ls, ind] = await Promise.all([
        getLineasPendientes(),
        getIndicadoresVinculacion(),
      ]);
      setLineas(ls);
      setIndicadores(ind);
    } catch (e) {
      setError(e?.message || 'Error al cargar líneas pendientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleVincular = async (linea, producto) => {
    setAccionando(linea?.id);
    try {
      await vincularLineaAProducto({
        lineaId: linea?.id,
        productoId: producto?.id,
        facturaId: linea?.factura_id,
        proveedorId: linea?.proveedor_id,
        metodo: 'MANUAL',
        confianza: 100,
        linea,
      });
      await cargar();
    } finally {
      setAccionando(null);
    }
  };

  const handleIgnorar = async (lineaId) => {
    setAccionando(lineaId);
    try {
      await ignorarLinea(lineaId);
      setLineas(prev => prev?.filter(l => l?.id !== lineaId));
    } catch (e) {
      setError(e?.message);
    } finally {
      setAccionando(null);
    }
  };

  const handleProcesarAuto = async () => {
    setProcesando(true);
    setProcesandoMsg(null);
    try {
      const stats = await procesarVinculacionesPendientes();
      setProcesandoMsg(`Procesadas: ${stats?.procesadas} · Vinculadas: ${stats?.vinculadas} · Pendientes: ${stats?.pendientes} · Errores: ${stats?.errores}`);
      await cargar();
    } catch (e) {
      setProcesandoMsg(`Error: ${e?.message}`);
    } finally {
      setProcesando(false);
    }
  };

  const kpiCards = [
    { label: 'Total productos', value: indicadores?.total_productos ?? '—', color: '#60A5FA' },
    { label: 'Con coste', value: indicadores?.productos_con_coste ?? '—', color: '#34D399' },
    { label: 'Sin coste', value: indicadores?.productos_sin_coste ?? '—', color: '#FB923C' },
    { label: 'Con proveedor', value: indicadores?.productos_con_proveedor ?? '—', color: '#A78BFA' },
    { label: 'Sin proveedor', value: indicadores?.productos_sin_proveedor ?? '—', color: '#F87171' },
    { label: 'Líneas vinculadas', value: indicadores?.lineas_vinculadas ?? '—', color: '#34D399' },
    { label: 'Líneas pendientes', value: indicadores?.lineas_pendientes ?? '—', color: ORANGE },
    { label: 'Revisar precio', value: indicadores?.productos_precio_revisar ?? '—', color: '#FBBF24' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-oranienbaum font-bold text-white text-lg">Productos por vincular</h2>
          <p className="font-oranienbaum text-white/40 text-xs mt-0.5">Líneas de facturas sin producto asociado en el catálogo</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={cargar} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={12} />Actualizar
          </button>
          <button
            onClick={handleProcesarAuto}
            disabled={procesando}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum font-bold transition-all disabled:opacity-50"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}35`, color: ACCENT }}>
            {procesando
              ? <><div className="w-3.5 h-3.5 rounded-full border-2 border-white/20 border-t-[#D6EF4E] animate-spin" />Procesando...</>
              : <><Icon name="Zap" size={13} />Procesar vinculaciones pendientes</>}
          </button>
        </div>
      </div>

      {/* Mensaje proceso automático */}
      {procesandoMsg && (
        <div className="p-3 rounded-xl text-xs font-oranienbaum" style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.25)', color: ACCENT }}>
          <Icon name="CheckCircle2" size={13} className="inline mr-2" />{procesandoMsg}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {kpiCards?.map((k, i) => (
          <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p className="font-oranienbaum font-bold text-lg" style={{ color: k?.color }}>{loading ? '...' : k?.value}</p>
            <p className="font-oranienbaum text-white/40 text-[10px] leading-tight mt-0.5">{k?.label}</p>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={14} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
        </div>
      ) : lineas?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.25)' }}>
            <Icon name="CheckCircle2" size={24} style={{ color: '#34D399' }} />
          </div>
          <p className="font-oranienbaum text-white/60 text-sm">No hay líneas pendientes de vincular</p>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-oranienbaum">
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Proveedor', 'Factura', 'Descripción', 'Referencia', 'EAN', 'Cant.', 'P. Unitario', 'Acciones']?.map(h => (
                    <th key={h} className="px-3 py-3 text-left text-white/40 font-normal whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineas?.map((linea, i) => (
                  <tr key={linea?.id} style={{
                    background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <td className="px-3 py-3 text-white/70 whitespace-nowrap max-w-[120px] truncate">{linea?.proveedor_nombre || '—'}</td>
                    <td className="px-3 py-3 text-white/50 whitespace-nowrap">{linea?.numero_factura || '—'}</td>
                    <td className="px-3 py-3 text-white/80 max-w-[200px]">
                      <p className="truncate" title={linea?.descripcion_original || linea?.descripcion}>
                        {linea?.descripcion_original || linea?.descripcion || '—'}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-white/50 font-mono whitespace-nowrap">{linea?.referencia_proveedor || '—'}</td>
                    <td className="px-3 py-3 text-white/50 font-mono whitespace-nowrap">{linea?.ean_proveedor || '—'}</td>
                    <td className="px-3 py-3 text-white/70 text-right whitespace-nowrap">{linea?.cantidad ?? '—'}</td>
                    <td className="px-3 py-3 text-white/70 text-right whitespace-nowrap">{fmt(linea?.precio_compra_neto || linea?.precio_unitario)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setBuscarModal(linea)}
                          disabled={accionando === linea?.id}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-40"
                          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
                          <Icon name="Link" size={10} />Vincular
                        </button>
                        <button
                          onClick={() => setCrearModal(linea)}
                          disabled={accionando === linea?.id}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-40"
                          style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', color: BLUE }}>
                          <Icon name="PackagePlus" size={10} />Crear
                        </button>
                        <button
                          onClick={() => handleIgnorar(linea?.id)}
                          disabled={accionando === linea?.id}
                          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] transition-all disabled:opacity-40"
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
                          <Icon name="EyeOff" size={10} />Ignorar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
            <p className="font-oranienbaum text-white/30 text-[10px]">{lineas?.length} líneas pendientes</p>
          </div>
        </div>
      )}

      {/* Modal buscar producto */}
      {buscarModal && (
        <BuscarProductoModal
          linea={buscarModal}
          onVincular={handleVincular}
          onClose={() => setBuscarModal(null)}
        />
      )}

      {/* Modal crear producto */}
      {crearModal && (
        <CrearProductoModal
          linea={crearModal}
          onCreado={cargar}
          onClose={() => setCrearModal(null)}
        />
      )}
    </div>
  );
};

export default ProductosVincular;
